'use client';

import { useEffect, useRef, useState, type ReactElement } from 'react';
import { cn } from '@fshr/shared-react';
import { imgOpt } from '@fshr/shared';
import { useTealium } from '@fshr/context/tealium-context';
import { experienceAvailabilityDetail } from '../plp-analytics';
import Image from '../image';
import BevSafeText from '../bev-safe-text';
import CategoryChip from './category-chip';
import AvailabilityIndicator from './availability-indicator';
import { shouldShowAvailability } from './availability';
import ExperiencePrice from './experience-price';
import ExperienceDuration from './experience-duration';
import type { CurrencySource, ProductAvailabilitySource } from './live-product-data';
import PlayButtonSmall from '../icon/play_button_small';
import PauseButtonSmall from '../icon/pause_button_small';
import type { AvailabilityContext, ExperienceFeatureCardData } from './types';
import { useTranslations } from '@fshr/context/i18n-context';

const LEARN_MORE_CLASS =
	'text-button2 text-button-primary underline decoration-solid underline-offset-[5px] [text-decoration-skip-ink:none] [text-decoration-thickness:0.085em]';

type Props = {
	card: ExperienceFeatureCardData;
	productAvailabilityStore?: ProductAvailabilitySource;
	userStore?: CurrencySource;
	availabilityContext?: AvailabilityContext;
	interactionType?: string;
	className?: string;
};

export default function ExperienceFeatureCard({
	card,
	productAvailabilityStore,
	userStore,
	availabilityContext,
	interactionType,
	className,
}: Props): ReactElement {
	const t = useTranslations();
	const videoRef = useRef<HTMLVideoElement>(null);
	const containerRef = useRef<HTMLElement>(null);
	const [isPlaying, setIsPlaying] = useState(false);
	const { trackLink } = useTealium();
	const hasVideo = Boolean(card.videoUrl);
	const showAvailability =
		Boolean(card.availability) && shouldShowAvailability(availabilityContext);
	const posterSrc = card.image?.defaultImage?.path
		? imgOpt({ path: card.image.defaultImage.path, cropping: card.image.defaultImage.cropping })
		: undefined;

	// Autoplay the video the first time the feature card scrolls into view.
	useEffect(() => {
		if (!hasVideo) return;
		const video = videoRef.current;
		const container = containerRef.current;
		if (!video || !container) return;
		if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return;

		const observer = new IntersectionObserver(
			entries => {
				entries.forEach(entry => {
					if (!entry.isIntersecting) return;
					void video
						.play()
						.then(() => setIsPlaying(true))
						.catch(() => undefined);
					observer.disconnect();
				});
			},
			{ threshold: 0.5 }
		);
		observer.observe(container);
		return () => observer.disconnect();
	}, [hasVideo]);

	const toggle = () => {
		const video = videoRef.current;
		if (!video) return;
		if (video.paused) {
			void video
				.play()
				.then(() => setIsPlaying(true))
				.catch(() => undefined);
		} else {
			video.pause();
			setIsPlaying(false);
		}
	};

	const cardBody = (
		<>
			<div className="flex flex-col gap-y-stack-s md:gap-y-stack-xs">
				<h3 className="text-h3 line-clamp-2 text-text-heading">
					<BevSafeText>{card.title}</BevSafeText>
				</h3>

				{card.category && (
					<CategoryChip icon={card.category.icon} label={card.category.label} variant="plain" />
				)}

				<ExperienceDuration
					productId={card.productId}
					productAvailabilityStore={productAvailabilityStore}
				/>

				{card.description && (
					<p className="text-body2 line-clamp-4 text-text-primary">
						<BevSafeText>{card.description}</BevSafeText>
					</p>
				)}

				<ExperiencePrice
					productId={card.productId}
					productAvailabilityStore={productAvailabilityStore}
					userStore={userStore}
				/>
			</div>

			<a
				href={card.href}
				target={card.newWindow ? '_blank' : undefined}
				rel={card.newWindow ? 'noopener' : undefined}
				onClick={() => {
					if (!interactionType) return;
					trackLink('interaction', {
						interaction_type: interactionType,
						interaction_name: 'experience card click',
						interaction_detail: experienceAvailabilityDetail(
							card.availability?.state,
							showAvailability
						),
					});
				}}
				className={`${LEARN_MORE_CLASS} self-start after:absolute after:inset-0 focus-visible:outline-2 focus-visible:outline-offset-2`}
			>
				{card.learnMoreLabel ?? t('Learn more')}
			</a>
		</>
	);

	return (
		<article ref={containerRef} className={cn('relative w-full overflow-hidden', className)}>
			<div className="relative aspect-square w-full overflow-hidden md:aspect-[2/1]">
				<Image
					roughSize="l"
					imagePrimitive={card.image}
					className="absolute inset-0 h-full w-full object-cover object-center"
				/>

				{hasVideo && (
					<video
						ref={videoRef}
						className="absolute inset-0 h-full w-full object-cover object-center"
						poster={posterSrc}
						muted
						loop
						playsInline
						preload="none"
					>
						<source src={card.videoUrl} type="video/mp4" />
					</video>
				)}

				{hasVideo && (
					<button
						type="button"
						onClick={toggle}
						aria-label={isPlaying ? t('Pause video') : t('Play video')}
						aria-pressed={isPlaying}
						className="absolute bottom-stack-s right-inline-s z-20 flex size-200 items-center justify-center rounded-full bg-img-overlay-40 fill-current text-text-on-image-primary transition-colors hover:bg-img-overlay-60 focus-visible:bg-img-overlay-60 focus-visible:outline-2 focus-visible:outline-offset-2 md:bottom-stack-l md:left-inline-l md:right-auto md:size-400"
					>
						{/* Figma uses the small icon at both sizes - its outline scales to 2px on desktop. */}
						{isPlaying ? (
							<PauseButtonSmall className="size-full" />
						) : (
							<PlayButtonSmall className="size-full" />
						)}
					</button>
				)}

				{/* Desktop: floating glass panel over the image, per design. */}
				<div
					data-testid="feature-card-desktop-panel"
					className="pointer-events-none absolute inset-0 hidden items-end justify-end md:flex"
				>
					<div className="pointer-events-auto flex w-full max-w-[400px] flex-col bg-surface-translucent backdrop-blur-[15px]">
						{showAvailability && card.availability && (
							<AvailabilityIndicator state={card.availability.state} />
						)}

						<div className="flex flex-col gap-y-stack-m p-300">{cardBody}</div>
					</div>
				</div>
			</div>

			{/* Mobile: image stays fully visible; content stacks below it instead of overlaying. */}
			<div data-testid="feature-card-mobile-panel" className="flex flex-col md:hidden">
				{showAvailability && card.availability && (
					<AvailabilityIndicator state={card.availability.state} />
				)}

				<div className="flex flex-col gap-y-stack-m bg-warm-stone-20 p-200">{cardBody}</div>
			</div>
		</article>
	);
}
